import { useJoinGrantGet } from "@sportsmet/pool-sdk/private";
import { Button } from "@sportsmet/ui";
import { useState } from "react";
import { ShareIcon } from "../../icons/actionIcons";
import {
  buildInviteMailtoHref,
  buildInviteShareSubject,
} from "../poolInvite/inviteShareHref";
import { buildPoolShareMessage } from "../poolInvite/poolShareText";

/** Active admins only. Read an existing grant on intent, never create a link. */
export function PoolCardShare({
  poolId,
  poolName,
}: {
  poolId: string;
  poolName: string;
}) {
  const grant = useJoinGrantGet({ poolId }, { enabled: false });
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  async function share() {
    if (busy) return;
    setBusy(true);
    try {
      const response = grant.data ?? (await grant.refetch()).data;
      const inviteUrl = response?.data?.joinUrl;
      if (!inviteUrl || !response?.data?.joinCode || response.error) {
        setUnavailable(true);
        return;
      }
      if (typeof navigator.share === "function") {
        await navigator.share({
          title: buildInviteShareSubject(poolName),
          text: buildPoolShareMessage({ poolName, inviteUrl }),
          url: inviteUrl,
        });
      } else {
        window.location.href = buildInviteMailtoHref({ poolName, inviteUrl });
      }
    } catch {
      // The native share sheet rejects when dismissed; never create a replacement link.
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={busy || unavailable || grant.isError}
      aria-label="Share this pool"
      aria-busy={busy}
      title={unavailable ? "Invite link unavailable" : undefined}
      data-testid={`pool-card-share-${poolId}`}
      onClick={() => {
        void share();
      }}
    >
      <ShareIcon />
    </Button>
  );
}
